import type {
  ClaimAttributeForm,
  IngressRequestForm,
} from '@dispatch/shared/types';
import { Section } from '@dispatch/web/ui';
import { Box, Divider, Grid, Stack, Typography } from '@mui/material';
import { UseFormGetValues } from 'react-hook-form';
import { startCase } from 'lodash';
import { format } from 'date-fns';
import { useCompany } from '@dispatch/shared/hooks';

export type IngressRequestReviewSectionProps = {
  getValues: UseFormGetValues<IngressRequestForm>;
  timeZone: string;
  preferredLanguages?:
    | {
        title: string;
        value: string;
      }[];
};

export function getClaimAttributeDisplayNameLookup(
  claimAttributes: ClaimAttributeForm[],
  companyAttributes: {
    integrationGUID: string;
    attributeDefinitionValues: {
      integrationGUID: string;
      displayName: string;
    }[];
  }[]
): Record<string, string> {
  const lookup: Record<string, string> = {};

  claimAttributes.forEach((claimAttr) => {
    const companyAttr = companyAttributes.find(
      (compAttr) =>
        compAttr.integrationGUID ===
        claimAttr.attributeDefinitionIntegrationGUID
    );

    claimAttr.selectedAttributeValuesVarchars.forEach((selectedObj) => {
      const valueObj = companyAttr?.attributeDefinitionValues.find(
        (defValue) => defValue.integrationGUID === String(selectedObj)
      );
      if (valueObj) {
        lookup[String(selectedObj) || ''] = valueObj.displayName;
      }
    });
  });

  return lookup;
}

export const IngressRequestReviewSection = ({
  getValues,
  preferredLanguages,
}: IngressRequestReviewSectionProps) => {
  const startCaseProperties = ['taskType', 'assignmentType', 'inspectionType'];
  const { perilOptions, company } = useCompany();

  const renderHelper = (object: unknown, title?: string) => {
    const renderObject = (object: unknown) => {
      return Object.entries(object as object).map(([key, value]) => {
        if (typeof value === 'object') {
          return renderHelper(value, key);
        }

        if (typeof value === 'boolean') {
          return (
            <Box display={'flex'} alignItems={'baseline'}>
              <Typography fontWeight={600}>{startCase(key)}: </Typography>
              <Typography>{value ? 'Yes' : 'No'}</Typography>
            </Box>
          );
        }

        if (!value) {
          return null;
        }

        return (
          <Box display={'flex'} alignItems={'baseline'}>
            <Typography fontWeight={600}>{startCase(key)}: </Typography>
            &nbsp;
            <Typography>
              {startCaseProperties.includes(key) ? startCase(value) : value}
            </Typography>
          </Box>
        );
      });
    };

    return <Section title={title}>{renderObject(object)}</Section>;
  };

  const renderClaimAttributes = () => {
    const attributesLookup: Record<string, string> =
      getClaimAttributeDisplayNameLookup(
        getValues('claimAttributes'),
        company.attributeDefinition
      );

    return getValues('claimAttributes').map((attribute) => {
      return (
        <Stack direction={'row'} display={'flex'} alignItems={'baseline'}>
          <Typography fontWeight={600} noWrap>
            {attribute.displayName}:{' '}
          </Typography>
          &nbsp;
          {attribute.selectedAttributeValuesVarchars.map((value, index) => {
            return <Typography>{attributesLookup[`${value}`]}</Typography>;
          })}
        </Stack>
      );
    });
  };

  const renderDateTime = (name: string, dateTime?: string) => {
    if (!dateTime) {
      return null;
    }

    return (
      <Box display={'flex'} alignItems={'baseline'}>
        <Typography fontWeight={600}>{name}: </Typography>
        &nbsp;
        <Typography>
          {format(new Date(dateTime), 'eeee MM/dd/yyyy hh:mm aa')}
        </Typography>
      </Box>
    );
  };

  // Rendered on their own below, or not collected by this flow.
  const {
    lossLocation: _lossLocation,
    dateOfLoss: _dateOfLoss,
    dateOfNotice: _dateOfNotice,
    jobId: _jobId,
    taskId: _taskId,
    sourcedFrom: _sourcedFrom,
    jobNumber: _jobNumber,
    ...claimWithoutLossLocation
  } = getValues('claim');

  claimWithoutLossLocation.peril = perilOptions?.find(
    (option) => option.value === claimWithoutLossLocation.peril
  )?.title;

  // Not collected by this flow: sent with their defaults, so not reviewed.
  const {
    mustEndBefore: _mustEndBefore,
    mustStartAfter: _mustStartAfter,
    templateId: _templateId,
    durationInMinutes: _durationInMinutes,
    ...assignmentHandlingWithoutDates
  } = getValues('assignmentHandling');

  if (assignmentHandlingWithoutDates.preferredLanguage) {
    assignmentHandlingWithoutDates.preferredLanguage = preferredLanguages?.find(
      (lang) => lang.value === assignmentHandlingWithoutDates?.preferredLanguage
    )?.title;
  }

  return (
    <Section title="Review">
      <Grid container spacing={2}>
        <Grid item xs={6}>
          {renderHelper(getValues('claim.lossLocation'), 'Loss Location')}
        </Grid>
        {getValues('contact.hasAlternateLocation').valueOf() && (
          <Grid item xs={6}>
            {renderHelper(
              getValues('contact.altLocation'),
              'Alternate Location'
            )}
          </Grid>
        )}
        <Divider style={{ marginTop: 20, marginBottom: 20 }} />
        <Grid item xs={12}>
          {renderHelper(claimWithoutLossLocation, 'Claim Information')}
          {renderDateTime('Date of Loss', getValues('claim.dateOfLoss'))}
          {renderDateTime('Date of Notice', getValues('claim.dateOfNotice'))}
        </Grid>
        <Divider style={{ marginTop: 20, marginBottom: 20 }} />
        <Grid item xs={6}>
          {renderHelper(getValues('contact.primary'), 'Primary Contact')}
        </Grid>
        {getValues('contact.hasSecondaryContact').valueOf() && (
          <Grid item xs={6}>
            {renderHelper(getValues('contact.secondary'), 'Secondary Contact')}
          </Grid>
        )}
        <Grid item xs={12}>
          {renderHelper(assignmentHandlingWithoutDates, 'Assignment Handling')}
        </Grid>
        <Grid item xs={12}>
          {renderHelper(getValues('additionalData'), 'Additional Data')}
        </Grid>
        <Grid item container xs={12}>
          <Section title="Other Information">
            <Grid item xs={12}>
              <Box display={'flex'} alignItems={'baseline'}>
                <Typography fontWeight={600}>Required Resource: </Typography>
                &nbsp;
                <Typography>{getValues('requiredResource.title')}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box display={'flex'} alignItems={'baseline'}>
                <Typography fontWeight={600}>Task Type: </Typography>
                &nbsp;
                <Typography>
                  {startCase(getValues('assignmentHandling.taskType'))}
                </Typography>
              </Box>
            </Grid>
          </Section>
        </Grid>
        {getValues('claimAttributes').length > 0 && (
          <Grid item xs={12}>
            <Section title="Claim Attributes">
              {renderClaimAttributes()}
            </Section>
          </Grid>
        )}
      </Grid>
    </Section>
  );
};
